import { Stack, Text } from '@fluentui/react'
import { Line } from 'react-chartjs-2'
import type { TooltipItem } from 'chart.js'
import { cleanMetricName } from '../../utils/metrics'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

type MetricTrendsChartProps = {
  dates: string[]
  metrics: Record<string, (number | null)[]>
}

const COLORS = [
  '#2B3A67',
  '#496A99',
  '#5B8A72',
  '#D4775B',
  '#8BACC4',
  '#B8C47E',
  '#C8C1B1',
  '#3D5A80',
]

const cardStyle = {
  root: {
    padding: '24px',
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    flex: 1,
  },
}

const MetricTrendsChart = ({ dates, metrics }: MetricTrendsChartProps) => {
  if (!dates.length) return null

  const metricNames = Object.keys(metrics)

  const chartData = {
    labels: dates,
    datasets: metricNames.map((name, i) => ({
      label: cleanMetricName(name),
      data: metrics[name],
      borderColor: COLORS[i % COLORS.length],
      backgroundColor: COLORS[i % COLORS.length],
      tension: 0.3,
      pointRadius: 4,
      pointHoverRadius: 6,
      spanGaps: true,
    })),
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      tooltip: {
        callbacks: {
          label: (ctx: TooltipItem<'line'>) => `${ctx.dataset.label}: ${(Number(ctx.raw) * 100).toFixed(1)}%`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 1,
        ticks: {
          callback: (value: string | number) => `${(Number(value) * 100).toFixed(0)}%`,
        },
      },
    },
  }

  return (
    <Stack styles={cardStyle} tokens={{ childrenGap: 12 }}>
      <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, color: '#605e5c' } }}>
        Metric Trends Over Time
      </Text>
      <div style={{ height: 350 }}>
        <Line data={chartData} options={options} />
      </div>
    </Stack>
  )
}

export default MetricTrendsChart
